import {
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useMutation } from '@tanstack/react-query'
import { observer } from 'mobx-react'
import React, { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import RightDrawer from '../../../../components/customElements/RightDrawer/RightDrawer'
import { getImageSrc } from '../../../../helpers/getImageSrc'
import { useInvalidateQueries } from '../../../../hooks/useInvalidateQueries'
import { updateFinancialSourceGroup } from '../../../../services/ApiService'
import { notifySuccess } from '../../../../services/ToastifyService/ToastifyService'
import {
  closeModalFn,
  openModalFn,
} from '../../../../utils/controllers/ModalController'
import { getComp } from '../../../../utils/utils/DI'
import TypedAccordion from '../../../SingleRequestPage/components/SingleRequestDrawer/elements/TypedAccordion'
import { RequestType } from '../../../SingleRequestPage/types/Request.type'
import {
  adminRequestsCheckboxStore,
  adminRequestsPaginationStore,
} from '../../pages/AdminRequestsPage'
import { newAssignDealerDrawerStore } from '../MassOperationDrawer/NewAssignDealerDrawerStore'
import { useRequestsTableData } from '../NewRequestsTable/hooks/useNewRequestsTableData'

const MassChangePaymentType = () => {
  const [value, setValue] = useState<any>(null)
  const { isChangePaymentOpen, setIsChangePaymentOpen } =
    newAssignDealerDrawerStore
  const invalidate = useInvalidateQueries()
  const { data } = useRequestsTableData(adminRequestsPaginationStore)
  const { selectedIds, resetAll } = adminRequestsCheckboxStore
  const selectedData =
    data?.content.filter((row) => selectedIds.includes(row.id)) ?? []
  const onClose = () => setIsChangePaymentOpen(false)
  const [paymentTypes, setPaymentTypes] = useState<any[]>([])

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['massChangeTypeSubsidy', value],
    mutationFn: () =>
      updateFinancialSourceGroup({
        orderIds: [...selectedIds],
        financialSourceId: value.id,
      }),
    onSuccess: () => {
      closeModalFn['progress-backdrop']?.()
      adminRequestsPaginationStore.resetSelectedRow()
      invalidate(['requests-table'])
      onClose()
      resetAll(true)
      notifySuccess('Тип оплаты заявок обновлён')
    },
    onError: () => {
      closeModalFn['progress-backdrop']?.()
    },
  })

  const onSubmit = () => {
    openModalFn['transaction-confirmation']?.({
      text1: `Подтвердить изменение `,
      value1: `${selectedIds.length}`,
      text2:
        selectedIds.length > 1
          ? `заявкам типа оплаты на`
          : `заявке типа оплаты на`,
      value2: `${value?.name}?`,
      action: mutateAsync,
      processText: `Выполняется изменение типа оплаты на ${value?.name}`,
    })
  }

  const updatePaymentTypes = () => {
    let items = getComp('StatusesStore').financialSources.filter(
      (el: any) => ![2, 4].includes(el.id),
    )
    // если все заявки в белом списке то тип оплаты 1 остается иначе убираем 1
    const isAllFullDotation = isFullDotation(selectedData)
    if (!isAllFullDotation) items = items.filter((el: any) => el.id !== 1)
    // если у всех заявок причина "замена" то тип оплаты 3 остается иначе убираем 3
    const isExchange = isAllExchange(selectedData)
    if (!isExchange) items = items.filter((el: any) => el.id !== 5)

    setPaymentTypes(items)
  }

  useEffect(() => {
    if (isChangePaymentOpen) updatePaymentTypes()
    if (!isChangePaymentOpen) setValue(null)
  }, [isChangePaymentOpen])

  return (
    // @ts-ignore
    <RightDrawer
      open={isChangePaymentOpen}
      onClose={onClose}
      titleComp={
        <Box className={'fx-col'}>
          <Typography variant={'subtitle1'} color={'common.white'}>
            <FormattedMessage id={'Заявок выбрано'} />: {selectedIds.length}
          </Typography>
          <Typography variant={'body1'} color={'#8FBFF6'}>
            <FormattedMessage id={'Изменение типа оплаты'} />
          </Typography>
        </Box>
      }
    >
      <TypedAccordion summary={'Тип оплаты'} defaultOpenState>
        <Box sx={{ display: 'flex' }}>
          <FormControl fullWidth>
            <InputLabel id='demo-simple-select-label'>
              <FormattedMessage id={'Оплата'} />
            </InputLabel>
            <Select
              value={value}
              label={'Оплата'}
              onChange={(e) => setValue(e.target.value)}
            >
              {paymentTypes.map((el: any) => (
                <MenuItem key={el.id} value={el}>
                  <Box className={'aln-center'}>
                    {el.imageSrc ? <img alt={''} src={el.imageSrc} /> : null}
                    <Box ml={1}>
                      <Typography
                        sx={{
                          fontWeight: '500',
                        }}
                        variant={'subtitle2'}
                      >
                        {el.name}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!!value && (
            <Box className={'aln-center'} ml={1.5}>
              <IconButton onClick={onSubmit} disabled={isPending}>
                <img alt={'Подтвердить'} src={getImageSrc('check')} />
              </IconButton>
              <IconButton onClick={() => setValue(null)} disabled={isPending}>
                <img
                  alt={'Отменить'}
                  src={getImageSrc('close-dark')}
                  width={20}
                  height={20}
                />
              </IconButton>
            </Box>
          )}
        </Box>
      </TypedAccordion>
    </RightDrawer>
  )
}

const isFullDotation = (selectedData: RequestType[]) =>
  selectedData.every((row) => {
    const districts =
      getComp('TerritoryStore').territories[row.territory as string]?.Territory
        .Districts
    const district = districts.find(
      (district: any) => district.Name === row.district,
    )
    if (!district) return false
    const locality = district.Localities.find(
      (locality: any) => locality.Name === row.locality,
    )
    if (!locality) return false
    return locality.AllowedFinancialSourceIds.some((id: number) => id === 1)
  })

const isAllExchange = (selectedData: RequestType[]) =>
  selectedData.every((item) => item.reasonId === 3)

export default observer(MassChangePaymentType)
